import { supabase } from "@/integrations/supabase/client";
import type { SyncQueueEntry } from "./offlineStore";
import { notifyAssignment, notifyDefectCreated } from "./notifications";

export const LWW_CONFLICT_PREFIX = "LWW_CONFLICT";

/**
 * Minimal structural facade over the Supabase query builder used by the generic
 * sync engine. The concrete table type is not known at compile time (the table
 * name is resolved dynamically per entity), so we model only the chainable
 * methods these handlers actually call. Each chain ultimately resolves to a
 * PostgREST-shaped `{ data, error, count }` result.
 */
interface SyncQueryResult {
  data?: { id?: unknown; updated_at?: unknown } | null;
  error?: { message: string } | null;
  count?: number | null;
}

interface SyncTableBuilder extends PromiseLike<SyncQueryResult> {
  insert(values: Record<string, unknown>): SyncTableBuilder;
  upsert(values: Record<string, unknown>, options?: { onConflict?: string }): SyncTableBuilder;
  update(
    values: Record<string, unknown>,
    options?: { count?: "exact" | "planned" | "estimated" },
  ): SyncTableBuilder;
  select(columns?: string): SyncTableBuilder;
  eq(column: string, value: unknown): SyncTableBuilder;
  single(): SyncTableBuilder;
  maybeSingle(): SyncTableBuilder;
}

export type SyncEntryTable = SyncTableBuilder;

export interface SyncEntryHandlerContext {
  entry: SyncQueueEntry;
  payload: Record<string, unknown>;
  // Dynamic Supabase table facade; entity tables differ but share the query methods used here.
  table: SyncEntryTable;
}

export type SyncEntryHandler = (context: SyncEntryHandlerContext) => Promise<void>;
type SyncEntryOperation = SyncQueueEntry["operation"];

interface UpdateTargetSnapshot {
  updatedAt: unknown;
  wasVisible: boolean;
}

async function processInsertEntry({
  entry,
  payload,
  table,
}: SyncEntryHandlerContext): Promise<void> {
  const insertedId = await insertEntryPayload(entry, payload, table);
  if (entry.entityType === "defect" && insertedId) {
    await triggerDefectInsertNotifications(payload, insertedId);
  }
}

async function insertEntryPayload(
  entry: SyncQueueEntry,
  payload: Record<string, unknown>,
  table: SyncEntryTable,
): Promise<string | null> {
  await prepareInsertPayload(entry, payload);

  if (await payloadAlreadyExists(payload, table)) {
    return null;
  }

  return insertPreparedPayload(payload, table);
}

async function prepareInsertPayload(
  entry: SyncQueueEntry,
  payload: Record<string, unknown>,
): Promise<void> {
  if (entry.entityType !== "defect") return;

  await enrichDefectInsertPayload(payload);
}

async function insertPreparedPayload(
  payload: Record<string, unknown>,
  table: SyncEntryTable,
): Promise<string | null> {
  const { data, error } = await table.insert(payload).select("id").single();
  if (error) throw new Error(error.message);

  return data?.id ? String(data.id) : null;
}

async function payloadAlreadyExists(
  payload: Record<string, unknown>,
  table: SyncEntryTable,
): Promise<boolean> {
  if (!payload.id) return false;

  const { data: existing } = await table.select("id").eq("id", payload.id).maybeSingle();
  return Boolean(existing);
}

async function processUpdateEntry({
  entry,
  payload,
  table,
}: SyncEntryHandlerContext): Promise<void> {
  if (entry.entityType === "inspection_response") {
    await upsertInspectionResponse(payload, table);
    return;
  }

  await processIdBasedUpdate(entry, payload, table);
}

async function upsertInspectionResponse(
  payload: Record<string, unknown>,
  table: SyncEntryTable,
): Promise<void> {
  // Inspection responses use a composite key, mirroring the online mutation path (TES-787).
  const { error } = await table.upsert(payload, { onConflict: "inspection_id,checklist_item_id" });
  if (error) throw new Error(error.message);
}

async function processIdBasedUpdate(
  entry: SyncQueueEntry,
  payload: Record<string, unknown>,
  table: SyncEntryTable,
): Promise<void> {
  const id = getUpdatePayloadId(payload);
  const targetSnapshot = await fetchUpdateTargetSnapshot(table, id);

  assertLocalVersionWins(entry, id, targetSnapshot.updatedAt);
  await applyIdBasedUpdate(entry.entityType, id, payload, table, targetSnapshot.wasVisible);
}

function getUpdatePayloadId(payload: Record<string, unknown>): string {
  const id = payload.id as string | undefined;
  if (!id) throw new Error("UPDATE requires id in payload");
  return id;
}

async function fetchUpdateTargetSnapshot(
  table: SyncEntryTable,
  id: string,
): Promise<UpdateTargetSnapshot> {
  const { data: existing, error } = await table.select("updated_at").eq("id", id).maybeSingle();
  if (error) throw new Error(error.message);

  return {
    updatedAt: existing?.updated_at,
    wasVisible: Boolean(existing),
  };
}

function assertLocalVersionWins(entry: SyncQueueEntry, id: string, serverUpdatedAt: unknown): void {
  if (!serverUpdatedAt) return;

  const serverTime = new Date(String(serverUpdatedAt)).getTime();
  const localTime = new Date(entry.timestamp).getTime();
  if (serverTime <= localTime) return;

  // Surface a conflict so processSyncQueue dead-letters the local edit instead of dropping it.
  throw new Error(
    `${LWW_CONFLICT_PREFIX}: ${entry.entityType} ${id} — server version is newer than the queued edit`,
  );
}

async function applyIdBasedUpdate(
  entityType: SyncQueueEntry["entityType"],
  id: string,
  payload: Record<string, unknown>,
  table: SyncEntryTable,
  wasVisibleBeforeUpdate: boolean,
): Promise<void> {
  const { id: _id, ...updatePayload } = payload;
  const { error, count } = await table.update(updatePayload, { count: "exact" }).eq("id", id);
  if (error) throw new Error(error.message);
  if (count === 0) {
    throw new Error(getZeroRowUpdateErrorMessage(entityType, id, wasVisibleBeforeUpdate));
  }
}

function getZeroRowUpdateErrorMessage(
  entityType: SyncQueueEntry["entityType"],
  id: string,
  wasVisibleBeforeUpdate: boolean,
): string {
  if (wasVisibleBeforeUpdate) {
    return `Update denied by RLS or permissions: ${entityType} ${id} was visible before sync, but UPDATE affected 0 rows`;
  }

  return `Update target not visible or updateable: ${entityType} ${id} may be outside the current RLS scope or no longer exist`;
}

async function enrichDefectInsertPayload(payload: Record<string, unknown>): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;
  if (!payload.created_by) payload.created_by = user.id;
  // TES-1141: verifying_qm is intentionally NOT defaulted to the creator here.
  // The DB trigger set_default_verifying_qm (migration 264) assigns it only
  // when the creator actually holds the QM role; otherwise it stays NULL so
  // any team QM can verify in one step.
}

async function triggerDefectInsertNotifications(
  payload: Record<string, unknown>,
  defectId: string,
): Promise<void> {
  try {
    const notification = getDefectNotification(payload, defectId);

    await notifyAssignedQm(notification);
    await notifyDefectCreated("__all_qms__", notification.title, defectId, notification.severity);
  } catch (err) {
    console.error("Post-sync notification error:", err);
  }
}

interface DefectNotification {
  assignedTo: string | null;
  defectId: string;
  severity: string;
  title: string;
}

function getDefectNotification(
  payload: Record<string, unknown>,
  defectId: string,
): DefectNotification {
  return {
    assignedTo: payload.assigned_to as string | null,
    defectId,
    severity: (payload.severity as string) || "medium",
    title: (payload.title as string) || "",
  };
}

async function notifyAssignedQm(notification: DefectNotification): Promise<void> {
  if (!notification.assignedTo) return;

  await notifyAssignment(
    notification.assignedTo,
    "defect",
    notification.title,
    notification.defectId,
  );
}

const syncEntryHandlers: Record<SyncEntryOperation, SyncEntryHandler> = {
  INSERT: processInsertEntry,
  UPDATE: processUpdateEntry,
};

export function getSyncEntryHandler(operation: unknown): SyncEntryHandler {
  if (!isSyncEntryOperation(operation)) {
    throw new Error(`Unsupported sync operation: ${String(operation)}`);
  }

  return syncEntryHandlers[operation];
}

function isSyncEntryOperation(operation: unknown): operation is SyncEntryOperation {
  return operation === "INSERT" || operation === "UPDATE";
}
